import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

/** The three markets (20260925200000_staff_markets.sql). */
export type MarketCode = 'ON' | 'QC_EN' | 'QC_FR';

export const MARKETS: { code: MarketCode; label: string; short: string }[] = [
  { code: 'ON', label: 'Ontario', short: 'ON' },
  { code: 'QC_EN', label: 'Quebec · English', short: 'QC-EN' },
  { code: 'QC_FR', label: 'Quebec · French', short: 'QC-FR' },
];

export function marketLabel(code: string | null | undefined): string | null {
  return MARKETS.find((m) => m.code === code)?.label ?? null;
}

/** One row of `staff_markets`. */
export interface StaffMarketsRow {
  profile_id: string;
  markets: MarketCode[];
  sees_all: boolean;
}

/**
 * Which markets' unclaimed work a staff member sees. `configured` false = no
 * row yet, which sees every market — nobody loses work by being left out of
 * the setup. A row with no markets and not sees-all is someone who doesn't take
 * leads (Jarred, François): no unclaimed work at all, unknown markets included.
 */
export interface StaffCoverage {
  configured: boolean;
  seesAll: boolean;
  markets: MarketCode[];
}

export function coverageFromRow(row: StaffMarketsRow | null): StaffCoverage {
  if (!row) return { configured: false, seesAll: true, markets: [] };
  return {
    configured: true,
    seesAll: row.sees_all,
    markets: row.markets ?? [],
  };
}

/** Whether the coverage limits what's shown at all. */
export function isScoped(c: StaffCoverage): boolean {
  return c.configured && !c.seesAll;
}

/** Whether this person takes leads at all. */
export function takesLeads(c: StaffCoverage): boolean {
  return !isScoped(c) || c.markets.length > 0;
}

/**
 * Whether an unclaimed conversation in `market` is this person's to see and be
 * alerted for. An unknown market is every lead-taker's, so it can never fall
 * through; someone who takes no leads is never shown one.
 */
export function coversMarket(
  c: StaffCoverage,
  market: string | null | undefined
): boolean {
  if (!isScoped(c)) return true;
  if (!takesLeads(c)) return false;
  if (!market) return true;
  return c.markets.includes(market as MarketCode);
}

/**
 * The PostgREST condition for "a market I cover" on `inbox_items.market`,
 * as the inside of an `or(...)`; null when nothing needs filtering. For someone
 * who takes no leads it matches nothing (no market is called 'none').
 */
export function marketCondition(c: StaffCoverage): string | null {
  if (!isScoped(c)) return null;
  return c.markets.length
    ? `market.is.null,market.in.(${c.markets.join(',')})`
    : 'market.eq.none';
}

/** A stable key for the coverage, so queries refetch when it changes. */
export function coverageKey(c: StaffCoverage | null): string {
  if (!c) return 'loading';
  if (!isScoped(c)) return 'all';
  return [...c.markets].sort().join(',') || 'none';
}

/**
 * The signed-in user's coverage; null until it has loaded. Follows edits live
 * (staff_markets is in the realtime publication), so an admin changing
 * someone's markets re-scopes their open inbox without a reload.
 */
export function useStaffCoverage(userId: string | null): StaffCoverage | null {
  const [coverage, setCoverage] = useState<StaffCoverage | null>(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const read = async () => {
      const { data, error } = await supabase
        .from('staff_markets')
        .select('profile_id, markets, sees_all')
        .eq('profile_id', userId)
        .maybeSingle();
      // A failed read (the table not there yet) must not hide anything.
      if (!cancelled)
        setCoverage(
          error
            ? coverageFromRow(null)
            : coverageFromRow(data as StaffMarketsRow | null)
        );
    };
    void read();
    const channel = supabase
      .channel(`staff-markets-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'staff_markets',
          filter: `profile_id=eq.${userId}`,
        },
        () => void read()
      )
      .subscribe();
    return () => {
      cancelled = true;
      void supabase.removeChannel(channel);
    };
  }, [userId]);

  return coverage;
}
